import { useMemo } from "react";
import type { BoardQuery } from "@salesboard/contracts";
import { useFilterSet, type FilterDefs } from "@/hooks/useFilterSet";
import { pipelineFilterQuery } from "@/features/opportunities/pipelineFilters";
import { usePipelineScope } from "./usePipelineScope";

/**
 * The board's URL state: the shared pipeline filters, plus the year it is scoped to.
 *
 * Deliberately **not** `useServerTable` — the board has no page and no sort, so wearing the
 * table seam would carry dead weight. It wears `useFilterSet` instead, which is the half
 * both views genuinely share, on the same `opportunities:*` namespace as the list. That
 * shared namespace is what makes the view toggle preserve the filter set, and the shared
 * *hook* is what stops the two views' filters from drifting apart again.
 */
export function useBoardParams(defs: FilterDefs) {
  const scope = usePipelineScope();
  const filters = useFilterSet("opportunities", defs);
  const { apiParams } = filters;

  // The filters through the one shared builder, so a chip and the cards beneath it cannot
  // describe different sets — a chip the view ignores is worse than no chip at all.
  const query = useMemo<BoardQuery>(
    () => ({
      ...(scope.year !== undefined ? { year: scope.year } : {}),
      ...pipelineFilterQuery(apiParams),
    }),
    [scope.year, apiParams],
  );

  return { ...filters, scope, query };
}
